/**
 * Whether a business can use its admin, based on its subscription row.
 *
 * - subscribed: a Stripe subscription that is active or trialing (a business
 *   that cancelled keeps access until the paid period ends, because Stripe
 *   keeps the subscription active until then).
 * - past_due: a payment failed; Stripe is retrying. Access continues, with a
 *   warning, until Stripe gives up and cancels.
 * - trial: free trial still running, no plan chosen yet.
 * - locked: trial over (or subscription ended) and no plan. Only Billing works.
 *
 * Missing data never locks anyone out: an unknown state counts as access.
 */

export type AccessState = 'subscribed' | 'past_due' | 'trial' | 'locked';

export interface SubscriptionLike {
  status: string;
  trial_ends_at: string | null;
  stripe_subscription_id: string | null;
  stripe_status: string | null;
}

export interface SubscriptionAccess {
  state: AccessState;
  /** Whole days of free trial left (trial state only), rounded up. */
  trialDaysLeft: number | null;
  trialEndsAt: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Countdown banner shows once this many days or fewer are left. */
export const TRIAL_BANNER_DAYS = 14;

export function getSubscriptionAccess(
  sub: SubscriptionLike | null | undefined,
  now: number = Date.now(),
): SubscriptionAccess {
  if (!sub) return { state: 'subscribed', trialDaysLeft: null, trialEndsAt: null };

  const stripeStatus = sub.stripe_subscription_id ? sub.stripe_status ?? sub.status : null;
  if (stripeStatus === 'active' || stripeStatus === 'trialing') {
    return { state: 'subscribed', trialDaysLeft: null, trialEndsAt: sub.trial_ends_at };
  }
  if (stripeStatus === 'past_due' || stripeStatus === 'unpaid' || sub.status === 'past_due') {
    return { state: 'past_due', trialDaysLeft: null, trialEndsAt: sub.trial_ends_at };
  }

  if (sub.status === 'trialing' && sub.trial_ends_at) {
    const end = new Date(sub.trial_ends_at).getTime();
    if (Number.isFinite(end) && end > now) {
      return {
        state: 'trial',
        trialDaysLeft: Math.max(1, Math.ceil((end - now) / DAY_MS)),
        trialEndsAt: sub.trial_ends_at,
      };
    }
  }

  // Legacy rows marked active without Stripe (set by hand) keep access.
  if (sub.status === 'active' && !sub.stripe_subscription_id) {
    return { state: 'subscribed', trialDaysLeft: null, trialEndsAt: sub.trial_ends_at };
  }

  return { state: 'locked', trialDaysLeft: null, trialEndsAt: sub.trial_ends_at };
}
